import React from 'react';
import { ArrowRight, CheckCircle, Clock, Image as ImageIcon, Lock, Shield, Upload, Zap } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import toolPages from '../lib/toolPages';
import { getToolTone } from '../lib/toolVisuals';
import Seo, { DEFAULT_LOGO, SITE_URL } from './Seo';

const Landing = () => {
    const navigate = useNavigate();

    const openToolFromDrop = (event) => {
        event.preventDefault();
        navigate('/tool');
    };

    return (
        <div className="page">
            <Seo
                title="Free Online Image Compressor | CompressImages.org"
                description="Compress images instantly without losing quality. Free, secure, browser-based image optimizer for JPEG, PNG, WebP, and more."
                path="/"
                jsonLd={{
                    '@context': 'https://schema.org',
                    '@type': 'WebSite',
                    name: 'CompressImages.org',
                    url: `${SITE_URL}/`,
                    publisher: {
                        '@type': 'Organization',
                        name: 'CompressImages.org',
                        logo: DEFAULT_LOGO,
                    },
                    potentialAction: {
                        '@type': 'SearchAction',
                        target: `${SITE_URL}/tools?q={search_term_string}`,
                        'query-input': 'required name=search_term_string',
                    },
                }}
            />

            <section className="hero">
                <div className="hero-badge">Free · Private · Browser-only</div>
                <h1 className="hero-title">
                    Compress images.<br />Keep the <span>quality.</span>
                </h1>
                <p className="hero-sub">
                    Reduce JPEG, PNG, and WebP file sizes instantly, right in your browser. Nothing is uploaded to a server.
                </p>
                <div className="btn-row">
                    <Link className="btn-primary" to="/tool">
                        <Upload size={16} />
                        Compress an image
                    </Link>
                    <Link className="btn-secondary" to="/tools">
                        Browse all tools
                        <ArrowRight size={16} />
                    </Link>
                </div>
                <div className="trust-strip">
                    <div className="trust-item"><Lock />No uploads, ever</div>
                    <div className="trust-item"><Zap />Instant in-browser</div>
                    <div className="trust-item"><CheckCircle />JPEG · PNG · WebP</div>
                </div>
            </section>

            <Link
                className="upload-zone"
                to="/tool"
                onDragOver={(event) => event.preventDefault()}
                onDrop={openToolFromDrop}
            >
                <div className="upload-icon"><Upload /></div>
                <div className="upload-title">Open the compressor to choose an image</div>
                <div className="upload-sub">All processing happens in your browser, so your images stay private</div>
                <div className="upload-formats">
                    <span className="fmt-badge">JPEG</span>
                    <span className="fmt-badge">PNG</span>
                    <span className="fmt-badge">WebP</span>
                    <span className="fmt-badge">AVIF</span>
                </div>
            </Link>

            <div className="divider"></div>

            <section>
                <div className="section-hd">
                    <h2>Practical tools for everyday image work</h2>
                    <p>Designed for website owners, ecommerce sellers, creators, and anyone who needs smaller files fast.</p>
                </div>
                <div className="feature-row">
                    <div className="feature-card">
                        <div className="feature-card-icon"><ImageIcon /></div>
                        <div className="feature-card-title">Format-specific output</div>
                        <div className="feature-card-text">Use focused tools for JPEG compression, PNG to WebP, image resizing, and target-size exports.</div>
                    </div>
                    <div className="feature-card">
                        <div className="feature-card-icon"><Clock /></div>
                        <div className="feature-card-title">Before/after comparison</div>
                        <div className="feature-card-text">Review original size, optimized size, dimensions, and savings before downloading the new copy.</div>
                    </div>
                    <div className="feature-card">
                        <div className="feature-card-icon"><Shield /></div>
                        <div className="feature-card-title">Completely private</div>
                        <div className="feature-card-text">Images are processed by your device. No file is sent to any server and no account is required.</div>
                    </div>
                </div>
            </section>

            <section>
                <div className="section-hd">
                    <h2>Popular tools</h2>
                    <p>Optimized for specific formats and common upload requirements.</p>
                </div>
                <div className="tools-grid">
                    {toolPages.slice(0, 5).map((tool) => (
                        <Link className="tool-card" key={tool.path} to={tool.path}>
                            <div className={`tool-card-icon ${getToolTone(tool.path)}`}><ImageIcon /></div>
                            <div>
                                <div className="tool-name">{tool.title}</div>
                                <div className="tool-desc">{tool.subtitle}</div>
                            </div>
                            <div className="tool-arrow"><ArrowRight /></div>
                        </Link>
                    ))}
                </div>
            </section>
        </div>
    );
};

export default Landing;
